const focusableElements = `
  summary, 
  a[href], 
  button:enabled, 
  [tabindex]:not([tabindex^='-']), 
  input:not([type=hidden]):enabled, 
  select:enabled,
  textarea:enabled
`;

const trapFocusHandlers = {};

/**
 * This class is used to trap the focus within a specific DOM element.
 * It is typically used for accessibility purposes, such as in modal dialogs,
 * so that focus does not move outside the dialog while it is open.
 *
 * @param {HTMLElement} container - The DOM element within which to trap the focus.
 * @param {HTMLElement} [elementToFocus=container] - The DOM element to be focused initially. If not provided, the container itself will be focused.
 */

export default class TrapFocus {
  constructor(container, elementToFocus = container) {
    this.el = container;
    this.focusableEls = this.el.querySelectorAll(focusableElements);
    const first = this.focusableEls[0];
    const last = this.focusableEls[this.focusableEls.length - 1];

    this.removeTrapFocus();

    trapFocusHandlers.focusin = (event) => {
      if (
        event.target !== container &&
        event.target !== last &&
        event.target !== first
      )
        return;

      document.addEventListener('keydown', trapFocusHandlers.keydown);
    };

    trapFocusHandlers.focusout = function focusoutHandler() {
      document.removeEventListener('keydown', trapFocusHandlers.keydown);
    };

    trapFocusHandlers.keydown = function keydownHandler(event) {
      if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
      // On the last focusable element and tab forward, focus the first element.
      if (event.target === last && !event.shiftKey) {
        event.preventDefault();
        first.focus();
      }

      //  On the first focusable element and tab backward, focus the last element.
      if (
        (event.target === container || event.target === first) &&
        event.shiftKey
      ) {
        event.preventDefault();
        last.focus();
      }
    };

    document.addEventListener('focusout', trapFocusHandlers.focusout);
    document.addEventListener('focusin', trapFocusHandlers.focusin);

    elementToFocus.focus();

    if (
      elementToFocus.tagName === 'INPUT' &&
      ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
      elementToFocus.value
    ) {
      elementToFocus.setSelectionRange(0, elementToFocus.value.length);
    }
  }

  removeTrapFocus(elementToFocus = null) {
    document.removeEventListener('focusin', trapFocusHandlers.focusin);
    document.removeEventListener('focusout', trapFocusHandlers.focusout);
    document.removeEventListener('keydown', trapFocusHandlers.keydown);

    if (elementToFocus) elementToFocus.focus();
  }
}
